import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { SelectItem } from '../../../types/core';
import { Island } from '../../../types/island';
import { MONTHS } from '../../../utils/date';
import { useAppDataContext } from '../../../contexts/appData';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useContractSelectOptions from '../../../hooks/useContractSelectOptions';
import { AnalyticsInfo, MonthlyInfo } from '../../../types/analytics';
import {
  JourneysGroupedDataItem,
  SpreadPassengersGroupedDataItem,
  CargoGroupedDataItem,
  FuelGroupedDataItem,
  PassengersGroupedDataItem,
  DataItem,
  YearlyData,
  GroupedDataItem,
} from './types';

export enum GlobalFilterType {
  Islands = 'islands',
  Contracts = 'contracts',
  Counties = 'counties',
}

export enum PassengersFilter {
  All = 'All',
  Islanders = 'Islanders',
  NonIslanders = 'NonIslanders',
}

const YEAR_MONTHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const EMPTY_JOURNEYS_DATA_ITEM: DataItem<JourneysGroupedDataItem> = {
  asScheduledJourneys: 0,
  cancelledJourneys: 0,
  reliefEventJourneys: 0,
  rescheduledJourneys: 0,
};

const EMPTY_SPREAD_PASSENGERS_DATA_ITEM: DataItem<SpreadPassengersGroupedDataItem> =
  {
    adultIslander: 0,
    adultNonIslander: 0,
    underEighteenIslander: 0,
    underEighteenNonIslander: 0,
    underFiveIslander: 0,
    underFiveNonIslander: 0,
    studentsIslander: 0,
    studentsNonIslander: 0,
    overSixtyIslander: 0,
    overSixtyNonIslander: 0,
  };

const EMPTY_CARGO_DATA_ITEM: DataItem<CargoGroupedDataItem> = {
  cargoHauled: 0,
};

const EMPTY_FUEL_DATA_ITEM: DataItem<FuelGroupedDataItem> = {
  fuelUsage: 0,
};

const useAnalyticsData = () => {
  const { t, language } = useLanguage();
  const { islands } = useAppDataContext();
  const { getYearAnalytics, getContracts } = useIotsApi();

  const [globalFilter, setGlobalFilter] = useState<{
    type: GlobalFilterType;
    values: string[];
    year: number;
  }>(() => ({
    type: GlobalFilterType.Islands,
    values: [],
    year: new Date().getFullYear(),
  }));

  const [passengersFilter, setPassengersFilter] = useState<PassengersFilter>(
    PassengersFilter.All,
  );

  const {
    data: contracts = [],
    isLoading: isLoadingContracts,
    error: contractsError,
    refetch: refetchContracts,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    // This data will only refetch on refresh or explicitely with an action
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const {
    data: fetchedAnalytics = {
      journeys: [],
      cargo: [],
      fuel: [],
      passengers: [],
      lastCalculationUpdate: '',
      lastCsvGenerations: [],
    },
    isLoading: isLoadingAnalytics,
    isFetching: isFetchingAnalytics,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['analytics', globalFilter.year],
    queryFn: () => getYearAnalytics(globalFilter.year),
    // This data will only refetch on refresh or explicitely with an action
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useQueryErrorNotifier([analyticsError, contractsError]);

  const islandsMap = useMemo(
    () =>
      islands.reduce(
        (acc, island) => ({ ...acc, [island.id]: island }),
        {} as Record<string, Island>,
      ),
    [islands],
  );

  const { contractsMap } = useContractSelectOptions(contracts, null);

  const {
    lastUpdate,
    allowedYears,
    islandItems,
    contractItems,
    countyItems,
    analytics,
  } = useMemo(() => {
    const islandItems: SelectItem[] = [];
    const contractItems: SelectItem[] = [];
    const countyItems: SelectItem[] = [];

    if (isLoadingAnalytics || isLoadingContracts) {
      return {
        lastUpdate: null,
        allowedYears: [],
        islandItems,
        contractItems,
        countyItems,
        analytics: fetchedAnalytics,
      };
    }

    const lastUpdate = dayjs(fetchedAnalytics.lastCalculationUpdate);

    const analytics: AnalyticsInfo = {
      journeys: [],
      passengers: [],
      cargo: [],
      fuel: [],
      lastCalculationUpdate: fetchedAnalytics.lastCalculationUpdate,
      lastCsvGenerations: fetchedAnalytics.lastCsvGenerations,
    };
    const islandIdsSet = new Set<string>();
    const contractIdsSet = new Set<string>();
    const countiesSet = new Set<string>();

    const processInfoItem = <T extends MonthlyInfo>(
      infoItem: T,
      resultList: T[],
    ) => {
      if (infoItem.year !== globalFilter.year) {
        return;
      }

      resultList.push(infoItem);

      islandIdsSet.add(infoItem.islandId);
      contractIdsSet.add(infoItem.contractId);
      countiesSet.add(infoItem.county);
    };

    // Journeys
    fetchedAnalytics.journeys.forEach(infoItem => {
      processInfoItem(infoItem, analytics.journeys);
    });
    // Passengers
    fetchedAnalytics.passengers.forEach(infoItem => {
      processInfoItem(infoItem, analytics.passengers);
    });
    // Cargo
    fetchedAnalytics.cargo.forEach(infoItem => {
      processInfoItem(infoItem, analytics.cargo);
    });
    // Fuel
    fetchedAnalytics.fuel.forEach(infoItem => {
      processInfoItem(infoItem, analytics.fuel);
    });

    // Prepare items lists
    islandIdsSet.forEach(islandId => {
      islandItems.push({
        value: islandId,
        label: islandsMap[islandId]?.names[language] ?? t('content.unknown'),
      });
    });
    islandItems.sort((a, b) => a.label.localeCompare(b.label));

    contractIdsSet.forEach(contractId => {
      contractItems.push(
        contractsMap[contractId] ?? {
          value: contractId,
          label: contractId,
        },
      );
    });
    contractItems.sort((a, b) => a.label.localeCompare(b.label));

    countiesSet.forEach(x => {
      countyItems.push({
        value: x,
        label: t(`counties.${x}`),
      });
    });
    countyItems.sort((a, b) => a.label.localeCompare(b.label));

    return {
      lastUpdate: lastUpdate.isValid() ? lastUpdate : null,
      allowedYears: analytics.lastCsvGenerations.map(({ year }) =>
        year.toString(),
      ),
      islandItems,
      contractItems,
      countyItems,
      analytics,
    };
  }, [
    isLoadingAnalytics,
    isLoadingContracts,
    fetchedAnalytics,
    globalFilter.year,
    contractsMap,
    islandsMap,
    language,
    t,
  ]);

  const {
    filterItems,
    filterSelectedItems,
    cargoData,
    fuelData,
    journeysData,
    spreadPassengersData,
  } = useMemo(() => {
    const filterItems: SelectItem[] = [];
    const filterSelectedItems: SelectItem[] = [];

    // Prepare filter elements
    const filterValuesSet = new Set<string>();

    const processFilterItem = (item: SelectItem) => {
      filterItems.push(item);
      if (globalFilter.values.includes(item.value)) {
        filterValuesSet.add(item.value);
        filterSelectedItems.push(item);
      }
    };

    if (globalFilter.type === GlobalFilterType.Islands) {
      islandItems.forEach(processFilterItem);
    } else if (globalFilter.type === GlobalFilterType.Contracts) {
      contractItems.forEach(processFilterItem);
    } else {
      countyItems.forEach(processFilterItem);
    }

    filterItems.sort((a, b) => a.label.localeCompare(b.label));
    filterSelectedItems.sort((a, b) => a.label.localeCompare(b.label));

    const hasFilters =
      filterValuesSet.size > 0 && filterItems.length > filterValuesSet.size;

    const shouldFilterOut = <T extends MonthlyInfo>(infoItem: T) => {
      if (!hasFilters) {
        return false;
      }
      const { contractId, county, islandId } = infoItem;

      const valueToFilter =
        globalFilter.type === GlobalFilterType.Islands
          ? islandId
          : globalFilter.type === GlobalFilterType.Contracts
            ? contractId
            : county;

      return !filterValuesSet.has(valueToFilter);
    };

    // Prepare grouped information
    const journeysData: YearlyData<JourneysGroupedDataItem> = {
      total: {
        ...EMPTY_JOURNEYS_DATA_ITEM,
      },
      grouped: [],
    };
    const spreadPassengersData: YearlyData<SpreadPassengersGroupedDataItem> = {
      total: {
        ...EMPTY_SPREAD_PASSENGERS_DATA_ITEM,
      },
      grouped: [],
    };
    const cargoData: YearlyData<CargoGroupedDataItem> = {
      total: {
        ...EMPTY_CARGO_DATA_ITEM,
      },
      grouped: [],
    };
    const fuelData: YearlyData<FuelGroupedDataItem> = {
      total: {
        ...EMPTY_FUEL_DATA_ITEM,
      },
      grouped: [],
    };

    const groupedJourneysMap: Record<number, JourneysGroupedDataItem> = {};
    const groupedPassengersMap: Record<
      number,
      SpreadPassengersGroupedDataItem
    > = {};
    const groupedFuelMap: Record<number, FuelGroupedDataItem> = {};
    const groupedCargoMap: Record<number, CargoGroupedDataItem> = {};

    const processInfoItem = <T extends MonthlyInfo, U extends GroupedDataItem>(
      infoItem: T,
      groupedItemsMap: Record<string, U>,
      emptyDataItem: DataItem<U>,
      groupDataItem: (groupedItem: U) => void,
    ) => {
      if (shouldFilterOut(infoItem)) {
        return;
      }
      // Months for grouped items start from 1, so they have to be adapted to start from 0
      const month = infoItem.month - 1;
      const groupedItem = groupedItemsMap[month] ?? {
        ...emptyDataItem,
        month: MONTHS[month],
      };

      groupDataItem(groupedItem);

      groupedItemsMap[infoItem.month - 1] = groupedItem;
    };

    // Journeys
    analytics.journeys.forEach(infoItem => {
      processInfoItem(
        infoItem,
        groupedJourneysMap,
        EMPTY_JOURNEYS_DATA_ITEM,
        groupedItem => {
          groupedItem.asScheduledJourneys += infoItem.asScheduledJourneys;
          groupedItem.cancelledJourneys += infoItem.cancelledJourneys;
          groupedItem.reliefEventJourneys += infoItem.reliefEventJourneys;
          groupedItem.rescheduledJourneys += infoItem.rescheduledJourneys;

          journeysData.total.asScheduledJourneys +=
            infoItem.asScheduledJourneys;
          journeysData.total.cancelledJourneys += infoItem.cancelledJourneys;
          journeysData.total.reliefEventJourneys +=
            infoItem.reliefEventJourneys;
          journeysData.total.rescheduledJourneys +=
            infoItem.rescheduledJourneys;
        },
      );
    });

    // Passengers
    analytics.passengers.forEach(infoItem => {
      processInfoItem(
        infoItem,
        groupedPassengersMap,
        EMPTY_SPREAD_PASSENGERS_DATA_ITEM,
        groupedItem => {
          groupedItem.adultIslander += infoItem.adultIslander;
          groupedItem.adultNonIslander += infoItem.adultNonIslander;
          groupedItem.overSixtyIslander += infoItem.overSixtyIslander;
          groupedItem.overSixtyNonIslander += infoItem.overSixtyNonIslander;
          groupedItem.studentsIslander += infoItem.studentsIslander;
          groupedItem.studentsNonIslander += infoItem.studentsNonIslander;
          groupedItem.underEighteenIslander += infoItem.underEighteenIslander;
          groupedItem.underEighteenNonIslander +=
            infoItem.underEighteenNonIslander;
          groupedItem.underFiveIslander += infoItem.underFiveIslander;
          groupedItem.underFiveNonIslander += infoItem.underFiveNonIslander;

          spreadPassengersData.total.adultIslander += infoItem.adultIslander;
          spreadPassengersData.total.adultNonIslander +=
            infoItem.adultNonIslander;
          spreadPassengersData.total.overSixtyIslander +=
            infoItem.overSixtyIslander;
          spreadPassengersData.total.overSixtyNonIslander +=
            infoItem.overSixtyNonIslander;
          spreadPassengersData.total.studentsIslander +=
            infoItem.studentsIslander;
          spreadPassengersData.total.studentsNonIslander +=
            infoItem.studentsNonIslander;
          spreadPassengersData.total.underEighteenIslander +=
            infoItem.underEighteenIslander;
          spreadPassengersData.total.underEighteenNonIslander +=
            infoItem.underEighteenNonIslander;
          spreadPassengersData.total.underFiveIslander +=
            infoItem.underFiveIslander;
          spreadPassengersData.total.underFiveNonIslander +=
            infoItem.underFiveNonIslander;
        },
      );
    });

    // Cargo
    analytics.cargo.forEach(infoItem => {
      processInfoItem(
        infoItem,
        groupedCargoMap,
        EMPTY_CARGO_DATA_ITEM,
        groupedItem => {
          groupedItem.cargoHauled += infoItem.cargoHauled;

          cargoData.total.cargoHauled += infoItem.cargoHauled;
        },
      );
    });

    // Fuel
    analytics.fuel.forEach(infoItem => {
      processInfoItem(
        infoItem,
        groupedFuelMap,
        EMPTY_FUEL_DATA_ITEM,
        groupedItem => {
          groupedItem.fuelUsage += infoItem.fuelUsage;

          fuelData.total.fuelUsage += infoItem.fuelUsage;
        },
      );
    });

    // Prepare data for the whole year (so not existant data is also represented)
    YEAR_MONTHS.forEach(month => {
      journeysData.grouped.push(
        groupedJourneysMap[month] ?? {
          ...EMPTY_JOURNEYS_DATA_ITEM,
          month: MONTHS[month],
        },
      );
      spreadPassengersData.grouped.push(
        groupedPassengersMap[month] ?? {
          ...EMPTY_SPREAD_PASSENGERS_DATA_ITEM,
          month: MONTHS[month],
        },
      );
      cargoData.grouped.push(
        groupedCargoMap[month] ?? {
          ...EMPTY_CARGO_DATA_ITEM,
          month: MONTHS[month],
        },
      );
      fuelData.grouped.push(
        groupedFuelMap[month] ?? {
          ...EMPTY_FUEL_DATA_ITEM,
          month: MONTHS[month],
        },
      );
    });

    return {
      filterItems,
      filterSelectedItems,
      journeysData,
      spreadPassengersData,
      cargoData,
      fuelData,
      groupedJourneys: journeysData.grouped,
      groupedPassengers: spreadPassengersData.grouped,
      groupedFuel: fuelData.grouped,
      groupedCargo: cargoData.grouped,
    };
  }, [
    globalFilter.type,
    globalFilter.values,
    analytics,
    islandItems,
    contractItems,
    countyItems,
  ]);

  const passengersData = useMemo<YearlyData<PassengersGroupedDataItem>>(() => {
    const aggregateDataItem: (
      originalDataItem: DataItem<SpreadPassengersGroupedDataItem>,
    ) => DataItem<PassengersGroupedDataItem> =
      passengersFilter === PassengersFilter.All
        ? data => ({
            adult: data.adultIslander + data.adultNonIslander,
            overSixty: data.overSixtyIslander + data.overSixtyNonIslander,
            students: data.studentsIslander + data.studentsNonIslander,
            underEighteen:
              data.underEighteenIslander + data.underEighteenNonIslander,
            underFive: data.underFiveIslander + data.underFiveNonIslander,
          })
        : passengersFilter === PassengersFilter.Islanders
          ? data => ({
              adult: data.adultIslander,
              overSixty: data.overSixtyIslander,
              students: data.studentsIslander,
              underEighteen: data.underEighteenIslander,
              underFive: data.underFiveIslander,
            })
          : data => ({
              adult: data.adultNonIslander,
              overSixty: data.overSixtyNonIslander,
              students: data.studentsNonIslander,
              underEighteen: data.underEighteenNonIslander,
              underFive: data.underFiveNonIslander,
            });
    return {
      total: aggregateDataItem(spreadPassengersData.total),
      grouped: spreadPassengersData.grouped.map(({ month, ...dataItem }) => ({
        ...aggregateDataItem(dataItem),
        month,
      })),
    };
  }, [spreadPassengersData, passengersFilter]);

  return {
    globalFilter,
    setGlobalFilter,
    passengersFilter,
    setPassengersFilter,
    lastUpdate,
    allowedYears,
    filterItems,
    filterSelectedItems,
    cargoData,
    fuelData,
    journeysData,
    passengersData,
    isFetchingAnalytics,
    refetch: () => {
      void refetchContracts();
      void refetchAnalytics();
    },
  };
};

export default useAnalyticsData;
